<template>
  <div>
    <div class="flex items-center justify-between mb-2">
      <span class="text-lg font-bold">Effect Statement</span>
      <button
        v-if="effectStatementText"
        @click="emit('clear-requested')"
        :aria-label="clearButtonLabel"
        class="text-xs font-medium text-toolkit-blue-outline hover:underline cursor-pointer"
      >
        Clear
      </button>
    </div>

    <div v-if="props.effectStatementNeedsUpdate && effectStatementText" class="bg-[#fff3cd] border border-[#ffc107] rounded flex items-center justify-between p-2 mb-3">
      <div class="flex items-center gap-3">
        <span class="material-icons-round text-text-2xl text-orange-100">info</span>
        <p class="text-sm text-orange-100">
          The Strengths & Needs statement has been updated. You may want to 
          <button @click="handleGenerateStatement" class="text-toolkit-blue-outline underline hover:no-underline">
            regenerate the Effect Statement
          </button>.
        </p>
      </div>
      <button @click="emit('update-flag', false)" class="text-gray-600 hover:text-gray-800">
        <span class="material-icons-round text-base">close</span>
      </button>
    </div>

    <div v-if="isGenerating" class="flex flex-col items-center justify-center gap-3 mb-3 bg-gray-50 border border-gray-300 rounded p-12">
      <span class="material-icons-round text-4xl text-blue-500 animate-spin">autorenew</span>
      <p class="text-md text-blue-500">Generating Effect Statement...</p>
    </div>

    <div v-else-if="effectStatementText" class="flex flex-col gap-3 mb-3">
      <WikiTextEditor
        :wikiText="editableText"
        @update:wikiText="(val: string) => editableText = val"
        :toEmbeddedText="toEmbeddedText"
        :toWikiText="toWikiText"
        :outer-class="''"
        :placeHolder="'Enter effect statement...'"
        :reset-value="effectStatementText"
        edited-text-type="effect_statement"
        label="Effect Statement"
        :hide-controls="true"
        @editModeUpdated="handleEditModeUpdated"
      />
    </div>

    <div v-if="!effectStatementText" class="flex items-center gap-2">
      <GbButton
        @click="handleGenerateStatement"
        gbTheme="bluePrimary"
        class="!px-4 !py-2 text-md"
        :disabled="isGenerating"
      >
        <span class="material-icons-round text-lg mr-1">add</span>
        {{ isGenerating ? 'Generating...' : 'Add Effect Statement' }}
      </GbButton>
    </div>

    <button
      v-else
      @click="handleGenerateStatement"
      :disabled="isGenerating"
      class="flex items-center gap-3 text-sm font-medium text-toolkit-blue-outline cursor-pointer disabled:opacity-50"
    >
      <span class="material-icons-round text-base">refresh</span>
      <span class="hover:underline">{{ isGenerating ? 'Generating...' : 'Regenerate Effect Statement' }}</span>
    </button>

    <div v-if="showEmptyStatementError" class="flex items-center gap-2 mt-2 px-2 py-2 rounded">
      <span class="material-icons-round text-text-2xl text-red-300">error</span>
      <span class="text-sm text-red-300">Please develop a Strengths & Needs statement above before adding an Effect Statement.</span>
    </div>

    <div v-if="showNoGradeLevelNeedError" class="flex items-center gap-2 mt-2 px-2 py-2 rounded">
      <span class="material-icons-round text-text-2xl text-red-300">error</span>
      <span class="text-sm text-red-300">The Strengths & Needs statement must include at least one need at grade level to generate an Effect Statement.</span>
    </div>

    <div v-if="showNoNeedsError" class="flex items-center gap-2 mt-2 px-2 py-2 rounded">
      <span class="material-icons-round text-text-2xl text-red-300">error</span>
      <span class="text-sm text-red-300">The Strengths & Needs statement must include at least one need to generate an Effect Statement.</span>
    </div>

    <div v-if="showGenericError" class="flex items-center gap-2 mt-2 px-2 py-2 rounded">
      <span class="material-icons-round text-text-2xl text-red-300">error</span>
      <span class="text-sm text-red-300">Error generating Effect Statement. Please try again.</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import GbButton from '@/components/GbButton.vue'
import WikiTextEditor from '@/components/editor/WikiTextEditor.vue'
import { postGenerateEffectStatement } from '@/presentlevels/api_v2'
import { transformResourceComponentsToWiki, transformWikiToResourceComponents } from '@/components/editor/wiki_tiptap_helpers'
import { nanoid } from 'nanoid'

const props = defineProps<{
  summaryUuid: string
  statementText: string
  saveToSummary?: boolean
  initialEffectStatement?: string
  effectStatementNeedsUpdate?: boolean
  subjectLabel?: string
}>()

const emit = defineEmits<{
  generated: [statementText: string]
  error: [error: Error]
  'update-flag': [needsUpdate: boolean]
  'clear-requested': []
}>()

const toEmbeddedText = transformWikiToResourceComponents
const toWikiText = transformResourceComponentsToWiki

const isGenerating = ref(false)
const effectStatementText = ref<string>(props.initialEffectStatement || '')
const editableText = ref<string>(props.initialEffectStatement || '')
const showEmptyStatementError = ref(false)
const showNoGradeLevelNeedError = ref(false)
const showNoNeedsError = ref(false)
const showGenericError = ref(false)

const clearButtonLabel = computed(() =>
  props.subjectLabel ? `Clear effect statement for ${props.subjectLabel}` : 'Clear effect statement'
)

const isAltAcademics = () => props.subjectLabel?.startsWith('Alt Academics:') ?? false

function clearAllErrors() {
  showEmptyStatementError.value = false
  showNoGradeLevelNeedError.value = false
  showNoNeedsError.value = false
  showGenericError.value = false
}

function handleEditModeUpdated(isEditMode: boolean) {
  if (!isEditMode && editableText.value !== effectStatementText.value) {
    effectStatementText.value = editableText.value
    emit('generated', editableText.value)
  }
}

function clearStatement() {
  effectStatementText.value = ''
  editableText.value = ''
  clearAllErrors()
  emit('generated', '')
}

// Expose clearStatement so parent can call it after confirmation
defineExpose({
  clearStatement
})

async function handleGenerateStatement() {
  clearAllErrors()
  if (!props.statementText.trim()) {
    showEmptyStatementError.value = true
    emit('error', new Error('No statement text available to generate effect statement'))
    return
  }
  
  emit('update-flag', false)

  isGenerating.value = true
  const trace = nanoid()
  
  try {
    const response = await postGenerateEffectStatement(
      props.summaryUuid,
      {
        statement_text: props.statementText,
        save_to_summary: props.saveToSummary ?? true,
        plw_trace: trace
      },
      true
    )

    if (response.effect_statement) {
      if (response.effect_statement === 'ERR_NO_ENROLLED_GRADE_NEED' || response.effect_statement === 'ERR_NO_NEEDS_FOUND') {
        // For Alt Academics, show error if no needs at all
        // For regular subjects, show error if no grade level needs
        if (isAltAcademics()) {
          showNoNeedsError.value = true
          emit('error', new Error('No needs found'))
        } else {
          showNoGradeLevelNeedError.value = true
          emit('error', new Error('No enrolled grade level need found'))
        }
      } else {
        effectStatementText.value = response.effect_statement
        editableText.value = response.effect_statement
        emit('generated', response.effect_statement)
        // Ensure the flag is cleared after successful generation
        emit('update-flag', false)
      }
    } else {
      // No effect statement in response - show generic error
      showGenericError.value = true
      emit('error', new Error('No effect statement returned from API'))
    }
  } catch (error) {
    console.error('Failed to generate effect statement:', error)
    showGenericError.value = true
    emit('error', error as Error)
  } finally {
    isGenerating.value = false
  }
}
</script>
